import React, { useState, useEffect } from 'react';
import api from '../api';
import { norm } from '../utils';
import { useConfirm } from './ConfirmProvider';

const FORM_INICIAL = {
  recheio_principal_id: '',
  recheio_secundario_id: '',
  camadas_principal: '2',
  camadas_secundario: '1',
  nome: '',
};

export default function GuiaCombinacoes() {
  const [combinacoes, setCombinacoes] = useState([]);
  const [recheios, setRecheios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [salvando, setSalvando] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    setLoading(true);
    try {
      const [resCombinacoes, resRecheios] = await Promise.all([
        api.get('/guia/combinacoes'),
        api.get('/guia/recheios'),
      ]);
      setCombinacoes(resCombinacoes.data);
      setRecheios(resRecheios.data);
    } catch {
      setErro('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }

  // ── Modal ─────────────────────────────────────────────────────────────────────
  function abrirNovo() {
    setForm(FORM_INICIAL);
    setEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(c) {
    setForm({
      recheio_principal_id: c.recheio_principal_id,
      recheio_secundario_id: c.recheio_secundario_id,
      camadas_principal: c.camadas_principal,
      camadas_secundario: c.camadas_secundario,
      nome: c.nome,
    });
    setEditando(c.id);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setEditando(null);
  }

  function handleChange(e) {
    const { name, value } = e.target;

    // Quando muda camadas_principal, ajusta secundario automaticamente (total = 3)
    if (name === 'camadas_principal') {
      const cp = Number(value);
      setForm(prev => ({
        ...prev,
        camadas_principal: cp,
        camadas_secundario: 3 - cp,
      }));
      return;
    }

    setForm(prev => ({ ...prev, [name]: value }));
  }

  // Auto-gera nome ao selecionar os dois recheios
  function handleRecheioChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      const p = recheios.find(r => r.id === Number(updated.recheio_principal_id));
      const s = recheios.find(r => r.id === Number(updated.recheio_secundario_id));
      if (p && s) {
        updated.nome = p.id === s.id ? p.nome : `${p.nome} + ${s.nome}`;
      }
      return updated;
    });
  }

  // ── Salvar ────────────────────────────────────────────────────────────────────
  async function salvar(e) {
    e.preventDefault();
    const cp = Number(form.camadas_principal);
    const cs = Number(form.camadas_secundario);
    if (cp + cs !== 3) {
      setErro('A soma das camadas deve ser 3');
      return;
    }

    setSalvando(true);
    setSucesso('');
    setErro('');

    const payload = {
      nome: form.nome,
      recheio_principal_id: Number(form.recheio_principal_id),
      recheio_secundario_id: Number(form.recheio_secundario_id),
      camadas_principal: cp,
      camadas_secundario: cs,
    };

    try {
      if (editando) {
        await api.put(`/guia/combinacoes/${editando}`, payload);
        setSucesso('Combinação atualizada!');
      } else {
        await api.post('/guia/combinacoes', payload);
        setSucesso('Combinação cadastrada!');
      }
      fecharModal();
      carregarDados();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao salvar combinação');
    } finally {
      setSalvando(false);
    }
  }

  async function desativar(id) {
    if (!await confirm('Desativar esta combinação?')) return;
    try {
      await api.delete(`/guia/combinacoes/${id}`);
      setSucesso('Combinação desativada.');
      carregarDados();
    } catch {
      setErro('Erro ao desativar combinação');
    }
  }

  // ── Render visual das camadas ─────────────────────────────────────────────────
  function renderCamadas(principal, nPrincipal, secundario, nSecundario) {
    const camadas = [];
    for (let i = 0; i < nPrincipal; i++) camadas.push({ nome: principal, tipo: 'principal' });
    for (let i = 0; i < nSecundario; i++) camadas.push({ nome: secundario, tipo: 'secundario' });
    return (
      <div className="flex gap-1">
        {camadas.map((c, i) => (
          <span
            key={i}
            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
              c.tipo === 'principal'
                ? 'bg-pink-100 text-pink-700'
                : 'bg-purple-100 text-purple-700'
            }`}
          >
            {c.nome}
          </span>
        ))}
      </div>
    );
  }

  // ── Filtros ──────────────────────────────────────────────────────────────────
  const [busca, setBusca] = React.useState('');

  const combinacoesFiltradas = combinacoes.filter(c =>
    !busca || norm(c.nome).includes(norm(busca)) ||
    norm(c.recheio_principal_nome).includes(norm(busca)) ||
    norm(c.recheio_secundario_nome).includes(norm(busca))
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-700">Combinações de Recheio</h1>
          <p className="text-gray-400 text-sm mt-1">Distribuição de camadas para bolos com 3 recheios</p>
        </div>
        <button onClick={abrirNovo}
          className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm">
          + Nova Combinação
        </button>
      </div>

      {/* Info — sanfona no celular (ocupava um terco da tela), aberta no desktop */}
      <details className="bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700 mb-4 sm:mb-5" open={typeof window !== 'undefined' && window.innerWidth >= 640}>
        <summary className="px-4 py-2.5 cursor-pointer font-semibold select-none">Como funciona</summary>
        <div className="px-4 pb-3 -mt-1">
          Para bolos com 3 camadas de recheio, você define quantas camadas vão de cada recheio.
          Ex: Brigadeiro + Mousse → 2 camadas Brigadeiro + 1 camada Mousse.
          Para 2 ou 4 camadas a divisão é automática (1+1 ou 2+2).
        </div>
      </details>

      {erro && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{erro}</div>}
      {sucesso && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">{sucesso}</div>}

      {/* Busca */}
      <div className="mb-6">
        <div className="relative max-w-sm">
          <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
          <input type="text" placeholder="Buscar por nome ou recheio..."
            value={busca} onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-300 focus:border-transparent" />
          {busca && (
            <button onClick={() => setBusca('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-sm">✕</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : combinacoesFiltradas.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Nenhuma combinação encontrada</p>
          <p className="text-gray-400 text-sm mt-1">{busca ? 'Tente ajustar a busca' : 'Clique em "+ Nova Combinação" para começar'}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto mb-4 hidden sm:block">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Nome</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Distribuição das camadas</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700" style={{width:'90px'}}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {combinacoesFiltradas.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="border border-gray-300 px-3 py-2 font-semibold text-gray-800">{c.nome}</td>
                    <td className="border border-gray-300 px-3 py-2">
                      {renderCamadas(c.recheio_principal_nome, c.camadas_principal, c.recheio_secundario_nome, c.camadas_secundario)}
                    </td>
                    <td className="border border-gray-300 px-2 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => abrirEditar(c)}
                          className="bg-gray-500 text-white p-1.5 rounded hover:bg-gray-600 transition-colors" title="Editar">✏️</button>
                        <button onClick={() => desativar(c.id)}
                          className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors" title="Remover">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Celular: a tabela espremia o nome em uma coluna de ~120px, quebrando
              "Brigadeiro de Maracuja" em cinco linhas, e repetia o nome do recheio
              em cada uma das tres etiquetas. Aqui o nome fica inteiro numa linha e
              as camadas viram contagem ("2x Brigadeiro"), que e a informacao real. */}
          <div className="sm:hidden flex flex-col gap-2 mb-4">
            {combinacoesFiltradas.map(c => {
              const camadas = [
                { nome: c.recheio_principal_nome, n: c.camadas_principal, cor: 'bg-pink-100 text-pink-700' },
                { nome: c.recheio_secundario_nome, n: c.camadas_secundario, cor: 'bg-purple-100 text-purple-700' },
              ].filter(x => x.n > 0);
              return (
                <div key={c.id} className="border border-gray-200 rounded-xl px-3 py-2.5 bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-gray-800 text-sm leading-snug min-w-0">{c.nome}</div>
                    <div className="flex gap-1 shrink-0">
                      {/* inline-flex + items/justify-center: o emoji herda a linha de
                          base do texto e, so com `text-base`, escapava do fundo
                          arredondado para cima e para a direita. */}
                      <button onClick={() => abrirEditar(c)} aria-label="Editar"
                        className="bg-gray-100 w-9 h-9 rounded-lg inline-flex items-center justify-center text-[15px] leading-none">✏️</button>
                      <button onClick={() => desativar(c.id)} aria-label="Remover"
                        className="bg-red-50 w-9 h-9 rounded-lg inline-flex items-center justify-center text-[15px] leading-none">🗑️</button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {camadas.map(x => (
                      <span key={x.nome} className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${x.cor}`}>
                        {x.n}× {x.nome}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rodapé */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700 text-sm">
                Total: {combinacoesFiltradas.length} {combinacoesFiltradas.length === 1 ? 'combinação' : 'combinações'}
              </span>
              <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                {recheios.length} recheios disponíveis
              </span>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      {modalAberto && (
        /* Sem teto de altura o modal ficava maior que a tela e, centralizado,
            escorregava metade para cima: o titulo sumia atras do cabecalho do app
            e nao havia como rolar ate ele. Com max-h + rolagem interna (e folha
            no celular, como os demais modais) o topo sempre aparece. */
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 modal-overlay-mobile" style={{ background: 'rgba(61,44,44,0.3)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto modal-content-sheet">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">
                {editando ? 'Editar Combinação' : 'Nova Combinação'}
              </h2>
              <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600 text-xl font-bold">
                ×
              </button>
            </div>

            <form onSubmit={salvar} className="px-6 py-5 space-y-4">

              {/* Recheios */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recheio principal *
                </label>
                <select
                  name="recheio_principal_id"
                  value={form.recheio_principal_id}
                  onChange={handleRecheioChange}
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                >
                  <option value="">Selecione...</option>
                  {recheios.map(r => (
                    <option key={r.id} value={r.id}>{r.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recheio secundário *
                </label>
                <select
                  name="recheio_secundario_id"
                  value={form.recheio_secundario_id}
                  onChange={handleRecheioChange}
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                >
                  <option value="">Selecione...</option>
                  {recheios.map(r => (
                    <option key={r.id} value={r.id}>{r.nome}</option>
                  ))}
                </select>
              </div>

              {/* Distribuição */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Distribuição das 3 camadas
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Camadas do principal</p>
                    <select
                      name="camadas_principal"
                      value={form.camadas_principal}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </div>
                  <div className="text-gray-400 text-sm mt-4">+</div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Camadas do secundário</p>
                    <div className="w-full border border-gray-100 bg-white rounded-lg px-3 py-2 text-sm text-gray-500">
                      {form.camadas_secundario} {Number(form.camadas_secundario) === 1 ? 'camada' : 'camadas'}
                    </div>
                  </div>
                  <div className="text-gray-400 text-sm mt-4">= 3</div>
                </div>

                {/* Preview visual */}
                {form.recheio_principal_id && form.recheio_secundario_id && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-400 mb-2">Preview:</p>
                    {renderCamadas(
                      recheios.find(r => r.id === Number(form.recheio_principal_id))?.nome || '',
                      Number(form.camadas_principal),
                      recheios.find(r => r.id === Number(form.recheio_secundario_id))?.nome || '',
                      Number(form.camadas_secundario),
                    )}
                  </div>
                )}
              </div>

              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da combinação
                </label>
                <input
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  placeholder="Gerado automaticamente"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Deixe em branco para gerar automaticamente pelo nome dos recheios.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  className="bg-pink-600 hover:bg-pink-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}